import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { DateFilterRow, PrizeDateFilterStateService } from '../services/prize-date-filter-state.service';

interface SelectOption {
  label: string;
  value: string;
}

/**
 * "Dates" filter section — a chain of date-condition rows (leading And/Or, field, fixed "is"
 * text, operator) matching the codebase's date-operator vocabulary already established for
 * AgGrid's date column filters (see ag-grid-filters.service.ts). Rows are the single real source
 * of truth behind PrizeDateFilterStateService's rows$: every row change is published there
 * immediately, which is what GeneratePrizesPanelComponent's "must have a Date filter" submit
 * guard reads from.
 */
@Component({
  selector: 'app-prize-date-filter',
  templateUrl: './prize-date-filter.component.html',
  styleUrls: ['./prize-date-filter.component.sass'],
})
export class PrizeDateFilterComponent implements OnInit, OnDestroy {
  readonly andOrOptions: SelectOption[] = [
    { label: 'And', value: 'and' },
    { label: 'Or', value: 'or' },
  ];

  readonly fieldOptions: SelectOption[] = [
    { label: 'Start', value: 'start' },
    { label: 'End', value: 'end' },
    { label: 'Rerelease Start', value: 'reReleaseStart' },
    { label: 'Rerelease End', value: 'reReleaseEnd' },
  ];

  readonly operatorOptions: SelectOption[] = [
    { label: 'Blank', value: 'blank' },
    { label: 'Not Blank', value: 'notBlank' },
    { label: 'Equals', value: 'equals' },
    { label: 'Greater than', value: 'greaterThan' },
    { label: 'Less than', value: 'lessThan' },
    { label: 'Not equal', value: 'notEqual' },
    { label: 'In range', value: 'inRange' },
  ];

  rowsForm: UntypedFormArray;

  private formValueSub: Subscription;

  constructor(
    private fb: UntypedFormBuilder,
    private dateFilterState: PrizeDateFilterStateService,
  ) {}

  ngOnInit(): void {
    this.rowsForm = this.fb.array(this.dateFilterState.getRows().map((row) => this.buildRowGroup(row)));
    this.publishRows();
    this.formValueSub = this.rowsForm.valueChanges.subscribe(() => this.publishRows());
  }

  ngOnDestroy(): void {
    this.formValueSub?.unsubscribe();
  }

  get rows(): UntypedFormGroup[] {
    return (this.rowsForm?.controls as UntypedFormGroup[]) || [];
  }

  addCondition(): void {
    this.rowsForm.push(
      this.buildRowGroup({
        id: this.generateRowId(),
        andOr: this.rows.length > 0 ? 'and' : null,
        field: 'start',
        operator: 'notBlank',
      }),
    );
    this.publishRows();
  }

  removeCondition(index: number): void {
    this.rowsForm.removeAt(index);
    this.publishRows();
  }

  private buildRowGroup(row: DateFilterRow): UntypedFormGroup {
    return this.fb.group({
      id: [row.id],
      andOr: [row.andOr],
      field: [row.field, Validators.required],
      operator: [row.operator, Validators.required],
    });
  }

  private publishRows(): void {
    const rows: DateFilterRow[] = this.rowsForm.value;
    this.dateFilterState.setRows(rows);
  }

  private generateRowId(): string {
    return `date-filter-row-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  }
}
